import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Theme } from "@mui/material/styles";
import { buildBrandTheme } from "./themeConfig";
import type { BrandName } from "./themeConfig";

/** Per-app brand metadata. The MUI theme itself is built by `buildBrandTheme`. */
export interface BrandConfig {
  name: BrandName;
  appName: string;
  logo: string;
  favicon: string;
  domains: string[];
}

export type BrandConfigMap = Partial<Record<BrandName, BrandConfig>>;

interface ThemeSwitcherContextType {
  currentBrand: BrandName;
  switchBrand: (brand: BrandName) => void;
  theme: Theme;
  isDevelopmentMode: boolean;
}

const ThemeSwitcherContext = createContext<
  ThemeSwitcherContextType | undefined
>(undefined);

const DEV_HOSTS = new Set(["localhost", "127.0.0.1"]);
const DEV_THEME_KEY = "dev-theme";

/**
 * Detects the active brand from the window hostname, checking `brandOrder`
 * most-specific-first so substrings don't mis-match.
 */
export function detectBrand(
  brandConfigs: BrandConfigMap,
  brandOrder: BrandName[],
): BrandName {
  const fallback = brandOrder[brandOrder.length - 1];
  if (typeof window === "undefined") {
    return fallback;
  }

  const hostname = window.location.hostname.toLowerCase();
  for (const brand of brandOrder) {
    const config = brandConfigs[brand];
    if (
      config?.domains.some((domain) => {
        const d = domain.toLowerCase();
        return (
          hostname === d || hostname.endsWith("." + d) || hostname.includes(d)
        );
      })
    ) {
      return brand;
    }
  }
  return fallback;
}

export interface ThemeSwitcherProviderProps {
  children: ReactNode;
  /** App-specific brand metadata (logos, domains, app name). */
  brandConfigs: BrandConfigMap;
  /** Detection priority; most specific brand first. Also the set of switchable brands. */
  brandOrder: BrandName[];
}

export const ThemeSwitcherProvider: React.FC<ThemeSwitcherProviderProps> = ({
  children,
  brandConfigs,
  brandOrder,
}) => {
  const isDevelopmentMode =
    typeof window !== "undefined" && DEV_HOSTS.has(window.location.hostname);

  const getInitialBrand = (): BrandName => {
    const detected = detectBrand(brandConfigs, brandOrder);
    if (isDevelopmentMode) {
      const stored = localStorage.getItem(DEV_THEME_KEY);
      if (stored && brandOrder.includes(stored as BrandName)) {
        return stored as BrandName;
      }
    }
    return detected;
  };

  const [currentBrand, setCurrentBrand] = useState<BrandName>(getInitialBrand);
  const [theme, setTheme] = useState<Theme>(() =>
    buildBrandTheme(getInitialBrand()),
  );

  // Re-detect on mount: production always follows hostname; dev keeps any dev-theme override.
  useEffect(() => {
    const detected = detectBrand(brandConfigs, brandOrder);
    if (!isDevelopmentMode) {
      if (detected !== currentBrand) {
        setCurrentBrand(detected);
        setTheme(buildBrandTheme(detected));
      }
    } else {
      const stored = localStorage.getItem(DEV_THEME_KEY);
      if (!stored && detected !== currentBrand) {
        setCurrentBrand(detected);
        setTheme(buildBrandTheme(detected));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchBrand = (brand: BrandName) => {
    setCurrentBrand(brand);
    setTheme(buildBrandTheme(brand));
    if (isDevelopmentMode) {
      localStorage.setItem(DEV_THEME_KEY, brand);
    }
    applyBrandChrome(brandConfigs[brand]);
  };

  useEffect(() => {
    applyBrandChrome(brandConfigs[currentBrand]);
  }, [currentBrand, brandConfigs]);

  return (
    <ThemeSwitcherContext.Provider
      value={{ currentBrand, switchBrand, theme, isDevelopmentMode }}
    >
      {children}
    </ThemeSwitcherContext.Provider>
  );
};

/** Updates document title and favicon to match the active brand. */
function applyBrandChrome(config: BrandConfig | undefined): void {
  if (!config || typeof document === "undefined") {
    return;
  }
  document.title = config.appName;
  const favicon = document.querySelector(
    'link[rel="icon"]',
  ) as HTMLLinkElement | null;
  if (favicon) {
    favicon.href = config.favicon;
  }
}

export const useThemeSwitcher = (): ThemeSwitcherContextType => {
  const context = useContext(ThemeSwitcherContext);
  if (!context) {
    throw new Error(
      "useThemeSwitcher must be used within a ThemeSwitcherProvider",
    );
  }
  return context;
};

/**
 * Returns the brand config for the currently active brand.
 * The calling app supplies its own `brandConfigs` map; the hook picks the active entry.
 */
export function useBranding(brandConfigs: BrandConfigMap): BrandConfig {
  const { currentBrand } = useThemeSwitcher();
  return brandConfigs[currentBrand]!;
}
